import { PhoneOutgoing, Trash2 } from 'lucide-react-native';
import { useState } from 'react';
import { Platform, Pressable, StyleSheet, View, type ViewStyle } from 'react-native';
import { HStack, Text, VStack } from '../../../components/primitives';
import { Badge } from '../../../components/primitives/Badge';
import { IconButton } from '../../../components/primitives/IconButton';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { VoiceCall, VoiceCallOutcome, VoiceCallStatus } from '../types';
import { OutcomeBadge } from './OutcomeBadge';

type BadgeColor = 'cyan' | 'green' | 'red' | 'amber' | 'purple' | 'blue' | 'neutral';

const STATUS_COLORS: Partial<Record<VoiceCallStatus, BadgeColor>> = {
  scheduled: 'amber',
  dialing: 'cyan',
  detecting: 'cyan',
  disclosure: 'cyan',
  discovery: 'cyan',
  handling: 'cyan',
  cta: 'purple',
  closing: 'purple',
  completed: 'green',
  no_answer: 'neutral',
  voicemail: 'neutral',
  hung_up_by_lead: 'red',
  technical_failure: 'red',
  cancelled: 'neutral',
};

const STATUS_LABEL_KEYS: Partial<Record<VoiceCallStatus, string>> = {
  scheduled: 'scheduled',
  dialing: 'dialing',
  completed: 'completed',
  no_answer: 'noAnswer',
  technical_failure: 'error',
  cancelled: 'cancelled',
};

const IN_PROGRESS_STATUSES: VoiceCallStatus[] = [
  'context_loaded',
  'dialing',
  'detecting',
  'disclosure',
  'discovery',
  'handling',
  'cta',
  'closing',
  'post_processing',
];

export function CallListItem({
  call,
  onPress,
  onDelete,
}: Readonly<{
  call: VoiceCall;
  onPress: (call: VoiceCall) => void;
  onDelete: (call: VoiceCall) => void;
}>) {
  const { t, language } = useTranslation();
  const [hovered, setHovered] = useState(false);
  const deletable = !IN_PROGRESS_STATUSES.includes(call.status);
  const locale = language === 'es' ? 'es-CO' : language === 'pt' ? 'pt-BR' : 'en-US';
  const date = new Date(call.createdAt).toLocaleDateString(locale, {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
  const duration = call.durationSeconds
    ? `${Math.floor(call.durationSeconds / 60)}:${String(call.durationSeconds % 60).padStart(2, '0')}`
    : '—';
  const statusLabelKey = STATUS_LABEL_KEYS[call.status];
  const statusLabel = statusLabelKey
    ? t(`voiceAgents.callsList.status.${statusLabelKey}`)
    : call.status.replaceAll('_', ' ');
  const leadName = call.lead?.fullName ?? call.toNumber ?? t('voiceAgents.callsList.unknownLead');

  const hoverProps = Platform.select({
    web: {
      onMouseEnter: () => setHovered(true),
      onMouseLeave: () => setHovered(false),
    },
    default: {},
  });

  return (
    <Pressable
      onPress={() => onPress(call)}
      accessible
      accessibilityRole="button"
      accessibilityLabel={`${t('voiceAgents.callsList.callToPrefix')} ${leadName}, ${statusLabel}`}
      style={styles.pressable}
    >
      <View style={[styles.card, hovered && styles.cardHover]} {...hoverProps}>
        <HStack spacing="md" align="start">
          <View style={styles.iconWell}>
            <PhoneOutgoing size={19} color={colors.primary.neon} strokeWidth={1.8} />
          </View>
          <VStack spacing="xs" style={styles.body}>
            <HStack spacing="sm" align="center" wrap>
              <Text
                variant="body"
                color={colors.text.primary}
                style={styles.name}
                numberOfLines={1}
              >
                {leadName}
              </Text>
              <Badge
                color={STATUS_COLORS[call.status] ?? 'neutral'}
                size="sm"
                accessibilityLabel={`${t('voiceAgents.callsList.statusPrefix')} ${statusLabel}`}
              >
                {statusLabel}
              </Badge>
            </HStack>
            <Text variant="caption" color={colors.text.secondary} numberOfLines={1}>
              {call.voiceAgent?.name ?? t('voiceAgents.callsList.unknownAgent')}
            </Text>
            <HStack spacing="xs" align="center" wrap>
              <Text selectable variant="caption" color={colors.text.tertiary} style={styles.meta}>
                {date}
              </Text>
              <View style={styles.dot} />
              <Text selectable variant="caption" color={colors.text.tertiary} style={styles.meta}>
                {duration}
              </Text>
              {call.toCountry ? (
                <>
                  <View style={styles.dot} />
                  <Text variant="caption" color={colors.text.tertiary} style={styles.meta}>
                    {call.toCountry}
                  </Text>
                </>
              ) : null}
            </HStack>
          </VStack>
          {deletable ? (
            <IconButton
              icon={<Trash2 size={16} color={colors.status.error.main} strokeWidth={1.8} />}
              size="sm"
              variant="ghost"
              accessibilityLabel={t('voiceAgents.callsList.deleteCall')}
              onPress={(event) => {
                event.stopPropagation?.();
                onDelete(call);
              }}
              hitSlop={8}
              style={styles.deleteButton}
            />
          ) : null}
        </HStack>
        {call.outcome ? (
          <View style={styles.outcome}>
            <OutcomeBadge outcome={call.outcome as VoiceCallOutcome} />
          </View>
        ) : null}
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  pressable: {
    flex: 1,
    minWidth: 0,
    ...(Platform.OS === 'web' ? ({ width: '100%' } as ViewStyle) : {}),
  },
  card: {
    minHeight: 112,
    padding: tokens.spacing.md,
    gap: tokens.spacing.sm,
    backgroundColor: colors.surface.row,
    borderWidth: 1,
    borderColor: colors.border.muted,
    borderRadius: tokens.radius.lg,
    ...(Platform.OS === 'web'
      ? ({
          cursor: 'pointer',
          transition: 'background-color 170ms ease, border-color 170ms ease',
        } as ViewStyle)
      : {}),
  },
  cardHover: {
    backgroundColor: colors.surface.rowHover,
    borderColor: colors.primary.borderActive,
  },
  iconWell: {
    width: 42,
    height: 42,
    borderRadius: tokens.radius.lg,
    backgroundColor: colors.primary.muted,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  body: {
    flex: 1,
    minWidth: 0,
  },
  name: {
    flexShrink: 1,
    minWidth: 0,
    fontWeight: tokens.fontWeight.bold,
  },
  meta: {
    fontSize: tokens.fontSize.xs,
    fontVariant: ['tabular-nums'],
  },
  dot: {
    width: 3,
    height: 3,
    borderRadius: 2,
    backgroundColor: colors.text.disabled,
  },
  deleteButton: {
    width: 34,
    height: 34,
  },
  outcome: {
    alignSelf: 'flex-start',
    paddingLeft: 54,
  },
});
